import { createSelector } from 'reselect';
import { filter, includes, values, uniqBy, isEmpty } from 'lodash-es';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { RootState } from './index';
import type { MenuGroup, Delivery, Location, Deliveryman, Call } from '../types';

dayjs.extend(utc);

// ==================== Menu Selectors ====================

const getMenuGroups = (state: RootState) => state.menu.groups;
const getMenuItems = (state: RootState) => state.menu.items;
const getGroupMembers = (state: RootState) => state.menu.groupMembers;
const getGroupGroupComplements = (state: RootState) => state.menu.groupGroupComplements;
const getItemGroupComplements = (state: RootState) => state.menu.itemGroupComplements;
const getCartItems = (state: RootState) => state.cart.cartItems;
const getSelectedCartItemUUID = (state: RootState) => state.cart.selectedItem;
const getCartPartGroupKey = (state: RootState) => state.cart.partialItems.group_key;
const getCartPartItems = (state: RootState) => state.cart.partialItems.cartItems;
const getCartPartNumberOfPieces = (state: RootState) => state.cart.partialItems.number_of_pieces;

/** Derives complement group list based on selected/last cart item */
function deriveComplementGroupList(
  selectedUUID: string,
  cartItems: Record<string, any>,
  groupGroupComplements: Record<number, number[]>,
  itemGroupComplements: Record<number, number[]>,
  partGroupKey: number | null,
  partItems: Record<string, any>,
): number[] {
  if (partGroupKey) {
    const lastPartUUID = Object.keys(partItems).pop();
    const lastPartItem = lastPartUUID ? partItems[lastPartUUID] : null;
    const groupList = groupGroupComplements[partGroupKey] || [];
    const itemList = lastPartItem ? (itemGroupComplements[lastPartItem.id] || []) : [];
    return [...groupList, ...itemList];
  }

  let referenceItem: any = null;
  if (selectedUUID in cartItems) {
    referenceItem = cartItems[selectedUUID];
  } else if (Object.keys(cartItems).length > 0) {
    const lastUUID = Object.keys(cartItems).pop()!;
    referenceItem = cartItems[lastUUID];
  }

  if (!referenceItem) return [];

  const groupList = groupGroupComplements[referenceItem.group_id] || [];
  const itemList = itemGroupComplements[referenceItem.id] || [];
  return [...groupList, ...itemList];
}

/** Returns groups to display in menu tabs */
export const selectDisplayGroups = createSelector(
  [getSelectedCartItemUUID, getCartItems, getGroupGroupComplements, getItemGroupComplements, getMenuGroups, getCartPartGroupKey, getCartPartItems],
  (selectedUUID, cartItems, groupGroupComplements, itemGroupComplements, menuGroups, partGroupKey, partItems) => {
    const complementList = deriveComplementGroupList(selectedUUID, cartItems, groupGroupComplements, itemGroupComplements, partGroupKey, partItems);
    return filter(menuGroups, (group: MenuGroup) =>
      (includes(complementList, group.id) || (group.base && !partGroupKey) || (partGroupKey === group.id)) && group.active
    );
  }
);

/** Is the next part the last part? */
export const selectIsLastPart = createSelector(
  [getCartPartItems, getCartPartNumberOfPieces],
  (partItems, numberOfPieces) => {
    return Object.keys(partItems).length + 1 === numberOfPieces && (numberOfPieces ?? 0) > 1;
  }
);

/** Get cart partial items */
export const selectCartPartItems = (state: RootState) => state.cart.partialItems.cartItems;
export const selectCartPartGroup = (state: RootState) => state.cart.partialItems.group_key;

// ==================== Combo Selectors ====================

const getComboEnabled = (state: RootState) => state.ui.comboEnabled;
const getCombo5Enabled = (state: RootState) => state.ui.combo5Enabled;

/** Returns Set of cart item UUIDs that should be gratis due to combo */
export const selectComboGratisUUIDs = createSelector(
  [getCartItems, getComboEnabled, getCombo5Enabled],
  (cartItems, comboEnabled, combo5Enabled): Set<string> => {
    if (!comboEnabled) return new Set();

    // Combo-eligible: combo=true and not already gratis (e.g. from pola-pola)
    const eligible = Object.values(cartItems)
      .filter(item => item.combo && !item.gratis);

    // Combo5 is a modifier: every 5 eligible items yields a gratis one
    // instead of every 4. Only meaningful when Combo is on.
    const groupSize = combo5Enabled ? 5 : 4;
    const freeCount = Math.floor(eligible.length / groupSize);
    if (freeCount === 0) return new Set();

    // Sort by price ascending — cheapest ones become gratis
    const sorted = [...eligible].sort((a, b) => a.price - b.price);

    const gratisUUIDs = new Set<string>();
    for (let i = 0; i < freeCount; i++) {
      gratisUUIDs.add(sorted[i].uuid);
    }
    return gratisUUIDs;
  }
);

// ==================== Delivery Selectors ====================

const getDeliveries = (state: RootState) => state.delivery;
const getRecordsDate = (state: RootState) => state.ui.recordsDate;
const getLocations = (state: RootState) => state.location;
const getDeliverymen = (state: RootState) => state.deliveryman;

export const selectDeliveriesForDate = createSelector(
  [getDeliveries, getRecordsDate],
  (deliveries, dateStr) => {
    const date = dayjs(dateStr);
    return values(deliveries)
      .filter((d: Delivery) => dayjs.utc(d.created_at).local().isSame(date, 'day'))
      .sort((a: Delivery, b: Delivery) => (a.created_at > b.created_at ? -1 : 1));
  }
);

export const selectTodaysDeliveries = createSelector(
  [getDeliveries],
  (deliveries) => {
    const today = dayjs().startOf('day');
    return values(deliveries).filter((d: Delivery) => dayjs.utc(d.created_at).local().isSame(today, 'day'));
  }
);

export const selectTodaysPendingDeliveries = createSelector(
  [selectTodaysDeliveries],
  (deliveries) => deliveries.filter((d: Delivery) => !d.dispatched_at && !d.canceled)
);

export const selectPendingDeliveryCountPerLocation = createSelector(
  [selectTodaysPendingDeliveries],
  (deliveries) => {
    const counts: Record<number, number> = {};
    deliveries.forEach((d: Delivery) => {
      if (d.location) {
        counts[d.location.id] = (counts[d.location.id] || 0) + 1;
      }
    });
    return counts;
  }
);

export const selectPendingOrderCountPerLocation = createSelector(
  [selectTodaysPendingDeliveries],
  (deliveries) => {
    const counts: Record<number, number> = {};
    deliveries.forEach((d: Delivery) => {
      if (d.location) {
        const orderCount = d.orders?.length || 0;
        counts[d.location.id] = (counts[d.location.id] || 0) + orderCount;
      }
    });
    return counts;
  }
);

export const selectLocationsMap = (state: RootState) => state.location;
export const selectDeliverymenMap = (state: RootState) => state.deliveryman;

// ==================== Location Selectors ====================

export const selectAgentLocations = (state: RootState) => state.agent.agentLocations;

export const selectDefaultLocation = createSelector(
  [selectAgentLocations],
  (locations) => locations.find((l: Location) => l.is_default) || locations[0] || null
);

export const selectSelectedLocation = (state: RootState) => state.ui.selectedLocation;
export const selectSelectedDeliveryType = (state: RootState) => state.ui.selectedDeliveryType;
export const selectDeliveryAddressDistance = (state: RootState) => state.ui.deliveryAddressDistance;

// ==================== Call Selectors ====================

const getCalls = (state: RootState) => state.call;

export const selectCallsForDate = createSelector(
  [getCalls, getRecordsDate],
  (calls, dateStr) => {
    const date = dayjs(dateStr);
    return values(calls)
      .filter((c: Call) => dayjs.utc(c.incoming_at).local().isSame(date, 'day'))
      .sort((a: Call, b: Call) => (a.incoming_at > b.incoming_at ? -1 : 1));
  }
);

export const selectTodaysCalls = createSelector(
  [getCalls],
  (calls) => {
    const today = dayjs().startOf('day');
    return values(calls)
      .filter((c: Call) => dayjs.utc(c.incoming_at).local().isSame(today, 'day'))
      .sort((a: Call, b: Call) => (a.incoming_at > b.incoming_at ? -1 : 1));
  }
);

export const selectActiveCallCount = createSelector(
  [selectTodaysCalls],
  (calls) => calls.filter((c: Call) => c.active === true).length
);

export const selectPendingOnlineOrderCount = createSelector(
  [selectTodaysDeliveries],
  (deliveries) => deliveries.filter((d: Delivery) => d.online_order === true && d.approved == null && !d.canceled).length
);

// ==================== Agent Selectors ====================

export const selectAgentMap = (state: RootState) => state.agent.agents;

// ==================== Checkin Selectors ====================

export const selectCheckins = (state: RootState) => state.checkin;

// ==================== Handoff Selectors ====================

export const selectHandoffLocation = (state: RootState) => state.ui.handoffLocation;

export const selectHandoffLocations = createSelector(
  [selectCheckins, getLocations],
  (checkinMap, locations) => {
    return Object.values(checkinMap).map((checkin) => locations[checkin.location]).filter(Boolean);
  }
);

export const selectHandoffDeliveryman = createSelector(
  [selectHandoffLocation, selectCheckins, getDeliverymen],
  (locationId, checkinMap, deliverymen) => {
    if (locationId == null) return undefined;
    const checkin = checkinMap[locationId];
    if (!checkin) return undefined;
    return deliverymen[checkin.deliveryman];
  }
);

export const selectHandoffDeliveries = createSelector(
  [selectHandoffLocation, selectCheckins, getDeliveries],
  (locationId, checkinMap, deliveries) => {
    if (locationId == null) return [];
    const checkin = checkinMap[locationId];
    if (!checkin) return [];
    const checkinTime = dayjs(checkin.time);
    return values(deliveries)
      .filter((d: Delivery) =>
        d.location?.id === locationId &&
        d.deliveryman != null && d.deliveryman === checkin.deliveryman &&
        !dayjs(d.dispatched_at).isBefore(checkinTime)
      )
      .sort((a: Delivery, b: Delivery) => (a.created_at >= b.created_at ? 1 : -1));
  }
);

// ==================== Feedback Selectors ====================

export const selectFeedbackMessages = (state: RootState) => state.feedback.messages;

// ==================== Stats Selectors ====================

export const selectLocationBalanceStats = (state: RootState) => state.stats.locationBalanceStats;
export const selectDeliveryMenStats = (state: RootState) => state.stats.deliveryMenStats;
export const selectItemsSoldStats = (state: RootState) => state.stats.itemsSoldStats;

// ==================== Trends Selectors ====================

export const selectTrendsRevenue = (state: RootState) => state.trends.revenue;
export const selectTrendsOperations = (state: RootState) => state.trends.operations;
export const selectTrendsItems = (state: RootState) => state.trends.items;
export const selectTrendsCalls = (state: RootState) => state.trends.calls;
export const selectTrendsCustomers = (state: RootState) => state.trends.customers;
export const selectTrendsPersonnel = (state: RootState) => state.trends.personnel;
export const selectTrendsLoading = (state: RootState) => state.trends.loading;

// ==================== UI Selectors ====================

export const selectUIVisibleComponents = (state: RootState) => state.ui.uiVisibleComponents;
export const selectLoadedCustomer = (state: RootState) => state.ui.loadedCustomer;
export const selectLoadedCall = (state: RootState) => state.ui.loadedCall;
export const selectCustomerAddressIndex = (state: RootState) => state.ui.loadedCustomerAddressIndex;
export const selectCustomerAddressCount = (state: RootState) => state.ui.loadedCustomerAddressCount;
export const selectModalCustomerDataLoad = (state: RootState) => state.ui.modalCustomerDataLoad;
export const selectComboEnabled = (state: RootState) => state.ui.comboEnabled;
export const selectGratisEnabled = (state: RootState) => state.ui.gratisEnabled;
export const selectSelectedDiscountValue = (state: RootState) => state.ui.selectedDiscountValue;
export const selectSelectedPaymentType = (state: RootState) => state.ui.selectedPaymentType;
export const selectOrderDelayTime = (state: RootState) => state.ui.orderDelayTime;
